import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { useRunnerState } from '@/composables/useRunnerState'
import { useRunnerUITransition } from '@/composables/useRunnerUITransition'
import { getContentfulUrl } from '@/utils/contentful'
import { CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'

const START_AT = 7 * 60
const END_AT = 7.75 * 60
const SPEED = 0.25

export type RunnerTimerProps = {
  className?: string
  variant?: 'default' | 'big'
  show: boolean
  play: boolean
  startAt?: number
  endAt?: number
  speed?: number
  isBroken?: boolean
}

const brokens = ['dhs%h', '%j:h3', '0$*xv', 'p0!h3', 'h3!0p', 'xv*0$', 'h3:j%', 'h%:j3', '0p!h3']

export default function RunnerTimer({
  className,
  variant,
  show,
  play,
  startAt = START_AT,
  endAt = END_AT,
  speed = SPEED,
  isBroken = false,
}: RunnerTimerProps) {
  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const [time, setTime] = useState(startAt)
  const [brokenIndex, setBrokenIndex] = useState(0)
  const inner = useRef<HTMLDivElement>(null)
  const elementRef = useRef<HTMLDivElement>(null)
  const brokenPathRef1 = useRef<SVGPathElement>(null)
  const brokenPathRef2 = useRef<SVGPathElement>(null)

  const { gameState } = useRunnerState()

  const { collectStatus, jauge } = gameState
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (inner.current && collectStatus === CollectStatus.Failed) {
        gsap
          .timeline()
          .to(
            inner.current,
            {
              duration: 0.1,
              x: -5,
              rotate: 2,
              ease: 'none',
            },
            0
          )
          .to(
            inner.current,
            {
              duration: 0.1,
              x: 5,
              rotate: -2,
              ease: 'none',
            },
            0.1
          )
          .to(
            inner.current,
            {
              duration: 0.1,
              x: -5,
              rotate: 2,
              ease: 'none',
            },
            0.2
          )
          .to(
            inner.current,
            {
              duration: 0.1,
              x: 5,
              rotate: -2,
              ease: 'none',
            },
            0.3
          )
          .to(
            inner.current,
            {
              duration: 0.1,
              x: 0,
              rotate: 0,
              ease: 'none',
            },
            0.4
          )
      }
    })

    return () => ctx.revert()
  }, [inner, collectStatus, gsap])

  // gameState.jauge

  const classNameText = (() => {
    switch (variant) {
      case 'big':
        return 'text-48'
      default:
        return 'text-32'
    }
  })()
  const classNameTextPart = (() => {
    switch (variant) {
      case 'big':
        return 'w-48'
      default:
        return 'w-32'
    }
  })()

  const tick = 100
  const applyTick = useCallback(() => {
    setTime((prev) => {
      if (play && prev < endAt) {
        return prev + (tick / 1000) * (jauge + 1) * speed
      } else {
        return prev
      }
    })
  }, [play, endAt, speed, jauge, tick, setTime])

  useLayoutEffect(() => {
    const interval = setInterval(applyTick, tick)

    return () => clearInterval(interval)
  }, [applyTick])

  const [brokenTimeline, setBrokenTimeline] = useState<gsap.core.Timeline | null>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const proxy = {
        value: 0,
      }
      const timeline = gsap
        .timeline({
          paused: true,
          repeat: -1,
          yoyo: true,
          onUpdate: () => {
            setBrokenIndex(Math.floor(proxy.value))
          },
        })
        .fromTo(proxy, { value: 0 }, { value: brokens.length, duration: 2, ease: 'power1.out' })
      setBrokenTimeline(timeline)
    })

    return () => ctx.revert()
  }, [gsap, setBrokenIndex])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isBroken) {
        gsap
          .timeline()
          .fromTo(
            [brokenPathRef2.current, brokenPathRef1.current],
            {
              drawSVG: '100% 100%',
            },
            {
              drawSVG: '0% 100%',
              duration: 0.3,
              stagger: 0.2,
              ease: 'power1.in',
            },
            0
          )
          .to(
            [brokenPathRef2.current, brokenPathRef1.current],
            {
              drawSVG: '0% 0%',
              duration: 0.2,
              stagger: 0.2,
              ease: 'power2.out',
            },
            0.3
          )
          .fromTo(
            elementRef.current,
            {
              rotate: 0,
            },
            {
              rotate: 3,
              duration: 0.5,
              ease: 'power1.in',
            },
            0.1
          )
          .to(
            elementRef.current,
            {
              rotate: -12,
              duration: 0.5,
              ease: 'back.out(1.2)',
            },
            0.5
          )
      }
    })
    if (isBroken) {
      brokenTimeline?.restart()
    } else {
      brokenTimeline?.pause()
    }

    return () => ctx.revert()
  }, [isBroken, brokenTimeline, brokenPathRef1, brokenPathRef2, elementRef, gsap])

  const minutes = Math.floor(time / 60)
  const prependZeroMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`

  const seconds = Math.floor(time % 60)
  const prependZeroSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`

  const { onEnter, onExit, timeout } = useRunnerUITransition(elementRef, { from: 'top' })

  const betterFormat = useImageFormatContext()
  const [alarmClockUrl, setAlarmClockUrl] = useState(null)
  const [alarmClockBrokenUrl, setAlarmClockBrokenUrl] = useState(null)
  useEffect(() => {
    setAlarmClockUrl(getContentfulUrl(`/src/assets/runner/alarm_clock.png`, { w: 300, fm: betterFormat }))
    setAlarmClockBrokenUrl(getContentfulUrl(`/src/assets/runner/alarm_clock_broken.png`, { w: 300, fm: betterFormat }))
  }, [betterFormat])

  return (
    <Transition
      in={show}
      nodeRef={elementRef}
      onEnter={onEnter}
      onExit={onExit}
      mountOnEnter
      unmountOnExit
      timeout={timeout}
    >
      <div ref={elementRef} className={`RunnerTimer relative ${className ? className : ''} origin-top-right`}>
        <div ref={inner} className='RunnerTimer__Inner relative'>
          <img src={alarmClockUrl} alt={t('misc.alarm_clock')} className={`block w-full`} />
          <img
            src={alarmClockBrokenUrl}
            alt={t('misc.alarm_clock')}
            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-333 ease-out-quart ${isBroken ? 'opacity-100' : 'opacity-0'}`}
          />

          {isBroken && (
            <>
              <svg
                className='absolute -left-[11px] -top-[4px]'
                width='43'
                height='27'
                viewBox='0 0 43 27'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  ref={brokenPathRef1}
                  d='M1 16.6035V5.13054C1 2.11481 4.65513 0.614561 6.7738 2.76067L16.0379 12.1447C17.6531 13.7808 20.4397 12.6371 20.4397 10.338V10.338C20.4397 8.03896 23.2262 6.89522 24.8414 8.53134L42 25.9121'
                  stroke='#EF563C'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
              <svg
                className='absolute -right-[27px] -bottom-[11px]'
                width='40'
                height='28'
                viewBox='0 0 40 28'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  ref={brokenPathRef2}
                  d='M30.9369 26.0586C29.4026 18.3039 26.9791 -0.130947 35.3902 3.12037C41.9087 5.64013 36.1028 23.0229 22.008 16.0647C7.91316 9.10656 15.7166 -2.34295 20.7334 3.1941C23.718 6.48808 20.3017 16.7972 1.34553 8.93958'
                  stroke='#EF563C'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </>
          )}

          <span
            className={`absolute inset-0 flex items-center justify-center text-black-blue text-numbers pt-10 gap-2 ${classNameText}`}
          >
            {isBroken ? (
              <>{brokens[brokenIndex % brokens.length]}</>
            ) : (
              <>
                <div className={`${classNameTextPart} justify-self-end text-right`}>{prependZeroMinutes}</div>
                <div className=''>:</div>
                <div className={`${classNameTextPart}`}>{prependZeroSeconds}</div>
              </>
            )}
          </span>
        </div>
      </div>
    </Transition>
  )
}
